import React, { useState, useCallback, useEffect, useRef } from "react";
import { DialogContainer, Button, TextField, FontIcon } from "react-md";
import GoogleMapReact from "google-map-react";
import NavigoDashboardUtils from "../services/NavigoDashboardUtils";

const navigoDashboardUtils = new NavigoDashboardUtils();

/* =========================
   API HELPERS
========================= */

/**
 * Generate a unique sessionToken (UUID v4)
 */
function generateSessionToken() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Autocomplete places
 * Backend response:
 * {
 *   suggestions: [{ placePrediction: {...} }]
 * }
 */
function fetchPlaces(query, sessionToken) {
  if (!query || query.length < 3) return Promise.resolve([]);

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getPlacesAutocomplete(
      {
        input: query,
        includedRegionCodes: ["in"],
        languageCode: "en",
        sessionToken,
      },
      (res) => {
        const list = (res?.suggestions || []).map((item) => {
          const p = item.placePrediction;
          const main = p?.structuredFormat?.mainText?.text || "";
          const secondary = p?.structuredFormat?.secondaryText?.text || "";

          return {
            placeId: p.placeId,
            label: secondary ? `${main}, ${secondary}` : main,
          };
        });

        resolve(list);
      },
      (err) => reject(err),
    );
  });
}

/**
 * placeId → coordinates
 * Backend response: [longitude, latitude]
 */
function getLatLngByPlaceId(placeId, sessionToken) {
  if (!placeId) return Promise.reject("placeId required");

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getCoordinatesByPlaceId(
      { placeId: placeId, sessionToken },
      (res) => {
        if (!res || res.latitude == null || res.longitude == null) {
          reject("Invalid coordinates response");
          return;
        }

        resolve({
          lat: res.latitude,
          lng: res.longitude,
        });
      },
      (err) => reject(err),
    );
  });
}

/**
 * coordinates → address object
 */
function getAddressByLatLng({ lat, lng }) {
  if (lat == null || lng == null) {
    return Promise.reject("lat/lng required");
  }

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getAddressByCoordinates(
      { latitude: lat, longitude: lng },
      (res) => resolve(res),
      (err) => reject(err),
    );
  });
}

/* =========================
   Hook
========================= */
export function useLocationDialog() {
  const [dialogState, setDialogState] = useState(null);

  const openLocationDialog = useCallback((initialData = {}) => {
    const safeData = initialData || {};

    return new Promise((resolve) => {
      setDialogState({
        visible: true,
        resolve,

        selectedLabel: safeData.selectedLabel || "",
        latLng: safeData.latLng || null,
        address: safeData.address || null,

        suggestions: [],
        loading: false,
        sessionToken: generateSessionToken(),
      });
    });
  }, []);

  const closeDialog = useCallback(
    (result) => {
      if (dialogState?.resolve) {
        dialogState.resolve(result);
      }
      setDialogState(null);
    },
    [dialogState],
  );

  return {
    dialogState,
    setDialogState,
    openLocationDialog,
    closeDialog,
  };
}

/* =========================
   Map Pin
========================= */
const Pin = () => (
  <FontIcon
    style={{
      color: "#EA4236",
      fontSize: "32px",
      transform: "translate(-50%, -100%)",
      cursor: "pointer",
    }}
  >
    location_pin
  </FontIcon>
);

/* =========================
   Dialog Component
========================= */
export function LocationSearchDialog({
  dialogState,
  setDialogState,
  closeDialog,
}) {
  const visible = dialogState?.visible || false;
  const query = dialogState?.query || "";
  const selectedLabel = dialogState?.selectedLabel || "";
  const suggestions = dialogState?.suggestions || [];
  const latLng = dialogState?.latLng || null;
  const address = dialogState?.address || null;
  const loading = dialogState?.loading || false;
  const sessionToken = dialogState?.sessionToken || "";
  const textFieldRef = useRef(null);

  /* =========================
     Auto-focus TextField
  ========================== */
  useEffect(() => {
    if (visible && textFieldRef.current) {
      setTimeout(() => {
        textFieldRef.current.focus();
      }, 100);
    }
  }, [visible]);

  /* =========================
     Fetch address for pre-seeded latLng (initialLocationData)
  ========================== */
  useEffect(() => {
    if (!visible || !latLng || address) return;

    (async () => {
      setDialogState((prev) => (prev ? { ...prev, loading: true } : prev));
      try {
        const addr = await getAddressByLatLng(latLng);
        setDialogState((prev) =>
          prev ? { ...prev, address: addr, loading: false } : prev,
        );
      } catch (e) {
        console.error(e);
        setDialogState((prev) => (prev ? { ...prev, loading: false } : prev));
      }
    })();
  }, [visible]);

  /* =========================
     Autocomplete Effect
  ========================== */
  useEffect(() => {
    // Clear suggestions when query becomes empty
    if (query === "") {
      setDialogState((prev) => (prev ? { ...prev, suggestions: [] } : prev));
      return;
    }

    if (!visible || query.length < 3) return;

    const timer = setTimeout(async () => {
      try {
        const list = await fetchPlaces(query, sessionToken);
        setDialogState((prev) =>
          prev ? { ...prev, suggestions: list } : prev,
        );
      } catch (e) {
        console.error(e);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, visible, setDialogState]);

  /* =========================
     Select Place
  ========================== */
  const handleSelectPlace = async (item) => {
    setDialogState((prev) => ({
      ...prev,
      loading: true,
      suggestions: [],
      selectedLabel: item.label,
    }));

    try {
      const coords = await getLatLngByPlaceId(item.placeId, sessionToken);
      const addr = await getAddressByLatLng(coords);

      setDialogState((prev) => ({
        ...prev,
        latLng: coords,
        address: addr,
        loading: false,
        sessionToken: generateSessionToken(),
      }));
    } catch (e) {
      console.error(e);
      setDialogState((prev) => ({ ...prev, loading: false }));
    }
  };

  if (!visible) return null;

  return (
    <DialogContainer
      id="location-search-dialog"
      visible
      width="900px"
      // onHide={() => closeDialog({ confirmed: false })}
      onHide={() => {}}
      dialogStyle={{ borderRadius: "10px", padding: "12px" }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Search location</h3>
        <div style={{ textAlign: "right" }}>
          {!latLng || loading ? (
            <></>
          ) : (
            <Button
              className={
                !latLng || loading ? "zy-btn-grey-1" : "zy-btn-orange-1"
              }
              disabled={!latLng || loading}
              onClick={() =>
                closeDialog({
                  confirmed: true,
                  latLng,
                  address,
                  selectedLabel,
                })
              }
            >
              Confirm
            </Button>
          )}

          <Button
            onClick={() => closeDialog({ confirmed: false })}
            className="zy-btn-cancel-1"
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Map + Search */}
      <div style={{ position: "relative", marginTop: 10, height: 350 }}>
        {/* Search Box */}
        <div
          style={{
            position: "absolute",
            zIndex: 2,
            width: 520,
            left: 10,
            top: 10,
            background: "#fff",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <TextField
            ref={textFieldRef}
            id="location-search-field"
            placeholder="Search location"
            value={selectedLabel || query}
            onChange={(v) =>
              setDialogState((prev) => ({
                ...prev,
                query: v,
                selectedLabel: "", // clear selected label on typing
              }))
            }
            multiline
            rows={1}
            style={{ width: "100%" }}
            maxRows={6}
            resize={{ min: 200 }}
            title={query}
            inlineIndicator={
              (selectedLabel || query) && (
                <button
                  onClick={() =>
                    setDialogState((prev) => ({
                      ...prev,
                      query: "",
                      selectedLabel: "",
                      suggestions: [],
                    }))
                  }
                  title="Clear search"
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#999",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#333")}
                  onMouseLeave={(e) => (e.target.style.color = "#999")}
                >
                  ✕
                </button>
              )
            }
          />

          {suggestions.length > 0 && (
            <div
              style={{
                border: "1px solid #ccc",
                maxHeight: 200,
                overflowY: "auto",
                marginTop: "8px",
                borderRadius: "4px",
              }}
            >
              {suggestions.map((item) => (
                <div
                  key={item.placeId}
                  style={{
                    padding: 8,
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                  }}
                  onClick={() => handleSelectPlace(item)}
                >
                  <FontIcon
                    style={{
                      color: "#26284E",
                      fontSize: "18px",
                      marginRight: "6px",
                    }}
                  >
                    location_on
                  </FontIcon>
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <GoogleMapReact
          bootstrapURLKeys={{ key: "AIzaSyDvCpz50pYwkg63zlD6iCodT75AGpqIhxc" }}
          center={latLng || { lat: 28.6139, lng: 77.209 }}
          zoom={14}
          onClick={async ({ lat, lng }) => {
            setDialogState((prev) => ({
              ...prev,
              latLng: { lat, lng },
              loading: true,
            }));

            try {
              const addr = await getAddressByLatLng({ lat, lng });
              setDialogState((prev) => ({
                ...prev,
                address: addr,
                loading: false,
              }));
            } catch (e) {
              console.error(e);
              setDialogState((prev) => ({ ...prev, loading: false }));
            }
          }}
        >
          {latLng && <Pin lat={latLng.lat} lng={latLng.lng} />}
        </GoogleMapReact>
      </div>

      {address && (
        <div style={{ marginTop: 12 }}>
          <strong>Address:</strong>
          <div>{address?.housing || "No location selected"}</div>
        </div>
      )}
      {latLng && (
        <div style={{ marginTop: 12 }}>
          <strong>Location selected:</strong>
          <div>{"Latitude: " + (latLng?.lat || "Not selected")}</div>
          <div>{"Longitude: " + (latLng?.lng || "Not selected")}</div>
        </div>
      )}
    </DialogContainer>
  );
}
